import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowRight,
    CheckCircle2,
    Loader2,
    DollarSign,
    Users,
    Sparkles,
    Zap,
    ShieldCheck,
    Wrench,
    Snowflake,
    Mail,
    Phone as PhoneIcon,
    ShoppingBag,
    Plane,
    Building2,
    Repeat,
} from "lucide-react";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
    { icon: <DollarSign size={20} />, title: "$0 free placement", body: "For eligible workplaces, the machine, install, restock and service is all free. It's a genuine no-cost amenity for your team." },
    { icon: <Repeat size={20} />, title: "Weekly restocks", body: "Every machine, every week. No emails asking us to please come and refill it — we're already scheduled in." },
    { icon: <Sparkles size={20} />, title: "Real product monitoring", body: "We watch what your team actually buys and swap out what isn't landing. Your machine gets more useful over time, not less." },
    { icon: <Snowflake size={20} />, title: "Summer pre-chill", body: "Ice-cold drinks ready the moment we walk out — no four-hour wait after a restock in a Perth heatwave." },
    { icon: <Users size={20} />, title: "Happier team", body: "24/7 access to snacks and drinks. Less \"I'm popping out to the servo\" — more time at their desks." },
    { icon: <Wrench size={20} />, title: "Zero maintenance", body: "We handle everything — stocking, cleaning, servicing, repairs, insurance. You literally don't have to think about it." },
    { icon: <ShieldCheck size={20} />, title: "Fully insured", body: "Every machine covered under our public liability and equipment insurance. No risk to your business as the host." },
    { icon: <Zap size={20} />, title: "Fast install", body: "From signed agreement to a live, stocked machine — typically within 4 weeks. Powered by a standard 240V outlet." },
];

const FAQS = [
    { q: "Does it really cost me nothing?", a: "For approved food & drink locations, yes. There's no cost to host — we cover the machine, installation, restocking, servicing and insurance. You provide floor space and a standard 240V power outlet." },
    { q: "What kinds of sites do you approve?", a: "Offices, schools, training facilities, factories, warehouses, construction sites and industrial venues — anywhere with enough staff and daily visitors to make the machine worthwhile for everyone. Full-time staff count and daily visitor numbers are the two key indicators for us." },
    { q: "How much space does a food & drink machine need?", a: "Between 1 and 2 square metres depending on machine type — combo machines are the most compact." },
                { q: "How quickly can you install?", a: "Setup time is typically within 4 weeks — sometimes faster if we've got stock in a local warehouse, but most units ship from overseas so 4 weeks is the honest expectation." },
    { q: "What if something breaks?", a: "We handle it. Every machine is monitored — we often know about issues before you do, and our Perth-based team responds fast." },
    { q: "Can I choose what's stocked?", a: "Absolutely. We work with you upfront to pick a product mix that suits your team, and adjust from there based on real sales data. We don't force manufacturer stock." },
    { q: "What about phone case vending machines?", a: "Different arrangement — phone case machines go into premium high-traffic venues (major malls, shopping centres, airports) on commercial lease terms that vary by site. Get in touch below and mention 'phone case machine' in your enquiry." },
    { q: "How do I get in touch outside this form?", a: "Email info@vendagain.com.au for new location enquiries, support@vendagain.com.au if you're an existing customer, or call 0410 784 187." },
];

export default function HostAMachinePage() {
    useSeo({
        title: "Free Vending Machines for Perth Workplaces",
        description:
            "Host a Vendagain vending machine at your Perth workplace — free placement (vending machine hire), weekly restocks, Perth-based service. Offices, schools, factories, warehouses, construction sites welcome.",
        canonicalPath: "/host-a-machine",
        keywords:
            "free vending machine Perth, vending machine hire Perth, free vending machine hire Perth, host vending machine Perth, workplace vending machines Perth, office vending Perth, factory vending Perth, warehouse vending Perth, construction site vending Perth, school vending Perth WA",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    });

    return (
        <>
            {/* HERO */}
            <section className="pt-12 lg:pt-20 pb-14 lg:pb-24 border-b border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-center">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            For Perth Workplaces
                        </span>
                        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]" data-testid="ham-hero-title">
                            A free vending machine.<br />
                            <span className="text-green-600">Paid for by us.</span>
                        </h1>
                        <p className="mt-5 text-lg text-neutral-600 leading-relaxed max-w-xl">
                            Host a Vendagain snack & drink machine at your
                            Perth office, school, factory, warehouse or
                            construction site. Free machine, free install, free
                            weekly restocks. You give us a small corner —
                            we do the rest.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#form" className="vg-pill vg-pill-primary" data-testid="ham-cta-form">
                                Enquire now
                                <ArrowRight size={14} />
                            </a>
                            <a href="#benefits" className="vg-pill vg-pill-outline">
                                See the benefits
                            </a>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-500">
                            {["$0 free placement", "Weekly restocks", "Perth-based team", "Fully insured"].map((t) => (
                                <div key={t} className="flex items-center gap-1.5">
                                    <CheckCircle2 size={14} className="text-green-500" />
                                    {t}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-neutral-950 text-white rounded-3xl p-8">
                        <div className="text-xs uppercase tracking-widest text-green-400 font-semibold mb-3">
                            At a glance
                        </div>
                        <div className="space-y-5">
                            {[
                                { k: "You provide", v: "1–2 m² and a 240V outlet" },
                                { k: "We provide", v: "Machine, install, weekly stock, servicing" },
                                { k: "Your cost", v: "$0" },
                                { k: "Setup time", v: "Typically within 4 weeks" },
                            ].map((row) => (
                                <div
                                    key={row.k}
                                    className="flex justify-between items-start gap-4 pb-4 border-b border-white/10 last:border-none last:pb-0"
                                >
                                    <div className="text-neutral-400 text-sm">{row.k}</div>
                                    <div className="text-white font-medium text-right text-sm">
                                        {row.v}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section id="benefits" className="py-16 lg:py-24 scroll-mt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Why host?
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            More than a machine in the corner.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {BENEFITS.map((b) => (
                            <div
                                key={b.title}
                                className="bg-white border border-neutral-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-md transition-all"
                            >
                                <div className="w-11 h-11 rounded-lg bg-green-500 text-white flex items-center justify-center mb-4">
                                    {b.icon}
                                </div>
                                <h3 className="text-base font-semibold mb-2">
                                    {b.title}
                                </h3>
                                <p className="text-sm text-neutral-600 leading-relaxed">
                                    {b.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-16 lg:py-24 bg-neutral-50 border-y border-neutral-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl mb-12">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            The process
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            From enquiry to a live machine — in weeks.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        {[
                            { n: "01", t: "Enquire", d: "Fill in the short form below. Takes 30 seconds." },
                            { n: "02", t: "Chat & visit", d: "We book a quick call and (if it looks right) a site visit." },
                            { n: "03", t: "Install", d: "Machine delivered, installed and fully stocked by our Perth team. Typically within 4 weeks." },
                            { n: "04", t: "Enjoy", d: "Weekly restocks begin. You never have to think about it again." },
                        ].map((s) => (
                            <div key={s.n} className="bg-white border border-neutral-200 rounded-2xl p-6">
                                <div className="font-mono text-xs text-neutral-400 mb-3">
                                    {s.n}
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{s.t}</h3>
                                <p className="text-sm text-neutral-600 leading-relaxed">{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FORM */}
            <LeadForm />

            {/* PHONE CASE MACHINE — separate CTA */}
            <section className="py-16 lg:py-24 bg-neutral-950 text-white border-t border-neutral-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16 items-start">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-400">
                            Phone case machines
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-white">
                            Manage a premium high-traffic venue?
                        </h2>
                        <p className="mt-4 text-neutral-300 leading-relaxed max-w-xl">
                            Our custom phone case printing machines are a
                            significant capital investment and perform best in
                            major shopping centres, malls and airports. We lease
                            floor space from the venue on commercial terms — every
                            site is priced individually based on foot traffic and
                            position.
                        </p>
                        <p className="mt-3 text-neutral-400 leading-relaxed max-w-xl text-sm">
                            Mention "phone case machine" in the enquiry form
                            above, or email <a href="mailto:info@vendagain.com.au" className="text-green-400 hover:text-white">info@vendagain.com.au</a> for a specific proposal.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <VenueTag icon={<ShoppingBag size={18} />} label="Shopping centres" />
                        <VenueTag icon={<Building2 size={18} />} label="Major malls" />
                        <VenueTag icon={<Plane size={18} />} label="Airports" />
                    </div>
                </div>
            </section>

            {/* CONTACT */}
            <section className="py-16 lg:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="mb-10 text-center">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            Prefer to reach out directly?
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Get in touch
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <ContactCard
                            icon={<Mail size={20} />}
                            label="New enquiries"
                            value="info@vendagain.com.au"
                            href="mailto:info@vendagain.com.au"
                        />
                        <ContactCard
                            icon={<Mail size={20} />}
                            label="Existing customer support"
                            value="support@vendagain.com.au"
                            href="mailto:support@vendagain.com.au"
                        />
                        <ContactCard
                            icon={<PhoneIcon size={20} />}
                            label="Call or SMS"
                            value="0410 784 187"
                            href="tel:+61410784187"
                        />
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="py-16 lg:py-24 bg-neutral-50 border-t border-neutral-200 scroll-mt-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <div className="mb-10 text-center">
                        <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                            FAQ
                        </span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                            Questions from Perth businesses
                        </h2>
                    </div>
                    <div className="space-y-3">
                        {FAQS.map((f, i) => (
                            <details
                                key={f.q}
                                className="group bg-white border border-neutral-200 rounded-2xl p-5 [&_summary::-webkit-details-marker]:hidden"
                                data-testid={`ham-faq-${i}`}
                            >
                                <summary className="cursor-pointer flex items-start justify-between gap-3 text-neutral-900 font-medium text-[15px]">
                                    <span>{f.q}</span>
                                    <span className="text-green-500 text-2xl leading-none group-open:rotate-45 transition-transform">
                                        +
                                    </span>
                                </summary>
                                <p className="mt-3 text-neutral-600 text-sm leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}

function VenueTag({ icon, label }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-green-500 text-white flex items-center justify-center flex-shrink-0">
                {icon}
            </div>
            <span className="text-sm font-medium text-white">{label}</span>
        </div>
    );
}

function ContactCard({ icon, label, value, href }) {
    return (
        <a
            href={href}
            className="block bg-white border border-neutral-200 rounded-2xl p-6 hover:border-neutral-900 hover:-translate-y-1 hover:shadow-md transition-all"
        >
            <div className="w-11 h-11 rounded-lg bg-green-500 text-white flex items-center justify-center mb-4">
                {icon}
            </div>
            <div className="text-xs text-neutral-500 uppercase tracking-widest font-semibold mb-1">
                {label}
            </div>
            <div className="text-base font-semibold text-neutral-900 break-all">
                {value}
            </div>
        </a>
    );
}

function LeadForm() {
    const [form, setForm] = useState({
        name: "",
        business_name: "",
        email: "",
        phone: "",
        location: "",
        business_type: "",
        machine_interest: "food_drink",
        full_time_staff: "",
        daily_visitors: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, form);
            setDone(true);
            toast.success("Enquiry sent — we'll be in touch shortly");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                (Array.isArray(detail) && detail[0]?.msg) ||
                (typeof detail === "string" ? detail : null) ||
                "Something went wrong sending your enquiry. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <section id="form" className="py-16 lg:py-24 scroll-mt-20">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-green-500 text-white flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 size={30} />
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
                        Thanks — we've got your enquiry.
                    </h2>
                    <p className="mt-4 text-neutral-600">
                        Our Perth team will review your details and be in touch
                        within 1–2 business days. Prefer to speak with us
                        directly? Call <a href="tel:+61410784187" className="text-green-600 font-semibold">0410 784 187</a> or email <a href="mailto:info@vendagain.com.au" className="text-green-600 font-semibold">info@vendagain.com.au</a>.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                        <Link to="/" className="vg-pill vg-pill-outline">
                            Back to home
                        </Link>
                        <Link to="/designer" className="vg-pill vg-pill-primary">
                            Try the Case Designer
                            <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="form" className="py-16 lg:py-24 scroll-mt-20">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <div className="text-center mb-10">
                    <span className="text-xs font-semibold uppercase tracking-widest text-green-600">
                        Register your interest
                    </span>
                    <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
                        Get a free machine at your workplace.
                    </h2>
                    <p className="mt-3 text-neutral-600 max-w-xl mx-auto">
                        Fill in a few details — the more you tell us, the
                        faster we can figure out if we're a fit.
                    </p>
                </div>

                <form
                    onSubmit={submit}
                    className="bg-white border border-neutral-200 rounded-3xl p-6 sm:p-8 space-y-5"
                    data-testid="lead-form"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Your name *">
                            <input type="text" required value={form.name} onChange={update("name")} data-testid="lead-name" className="vg-input" placeholder="Jane Smith" />
                        </Field>
                        <Field label="Business name">
                            <input type="text" value={form.business_name} onChange={update("business_name")} data-testid="lead-business" className="vg-input" placeholder="Acme Manufacturing" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Email *">
                            <input type="email" required value={form.email} onChange={update("email")} data-testid="lead-email" className="vg-input" placeholder="jane@acme.com.au" />
                        </Field>
                        <Field label="Phone">
                            <input type="tel" value={form.phone} onChange={update("phone")} data-testid="lead-phone" className="vg-input" placeholder="04xx xxx xxx" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Location / suburb">
                            <input type="text" value={form.location} onChange={update("location")} data-testid="lead-location" className="vg-input" placeholder="Kewdale, Perth CBD…" />
                        </Field>
                        <Field label="Type of workplace">
                            <select value={form.business_type} onChange={update("business_type")} data-testid="lead-business-type" className="vg-input">
                                <option value="">Choose one…</option>
                                <option>Office / co-working</option>
                                <option>School / training facility</option>
                                <option>Factory</option>
                                <option>Warehouse</option>
                                <option>Construction site</option>
                                <option>Industrial / trade site</option>
                                <option>Shopping centre / mall</option>
                                <option>Airport</option>
                                <option>Other</option>
                            </select>
                        </Field>
                    </div>

                    {/* NEW — the key qualifying questions */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Full-time staff on site">
                            <select value={form.full_time_staff} onChange={update("full_time_staff")} data-testid="lead-staff" className="vg-input">
                                <option value="">Choose a range…</option>
                                <option>Under 10</option>
                                <option>10–25</option>
                                <option>25–50</option>
                                <option>50–100</option>
                                <option>100–250</option>
                                <option>250+</option>
                            </select>
                        </Field>
                        <Field label="Avg. daily visitors with machine access">
                            <select value={form.daily_visitors} onChange={update("daily_visitors")} data-testid="lead-visitors" className="vg-input">
                                <option value="">Choose a range…</option>
                                <option>Under 25</option>
                                <option>25–50</option>
                                <option>50–100</option>
                                <option>100–250</option>
                                <option>250–500</option>
                                <option>500+</option>
                            </select>
                        </Field>
                    </div>

                    <Field label="Which machine are you interested in?">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {[
                                { v: "food_drink", l: "Food & Drink" },
                                { v: "phone_case", l: "Phone Case" },
                                { v: "both", l: "Both" },
                                { v: "unsure", l: "Not sure" },
                            ].map((opt) => (
                                <button
                                    key={opt.v}
                                    type="button"
                                    onClick={() => setForm({ ...form, machine_interest: opt.v })}
                                    className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                                        form.machine_interest === opt.v
                                            ? "bg-neutral-950 text-white border-neutral-950"
                                            : "bg-white text-neutral-800 border-neutral-200 hover:border-neutral-900"
                                    }`}
                                    data-testid={`lead-interest-${opt.v}`}
                                >
                                    {opt.l}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <Field label="Anything else?">
                        <textarea rows={4} value={form.message} onChange={update("message")} data-testid="lead-message" className="vg-input" placeholder="Operating hours, ideal spot in your venue, product preferences…" />
                    </Field>

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="vg-pill vg-pill-accent w-full justify-center text-base py-3"
                            data-testid="lead-submit"
                        >
                            {submitting ? (
                                <><Loader2 size={16} className="animate-spin" />Sending…</>
                            ) : (
                                <>Send enquiry<ArrowRight size={16} /></>
                            )}
                        </button>
                        <p className="text-xs text-neutral-500 text-center mt-3">
                            We'll only use your details to respond to this
                            enquiry. No spam, ever.
                        </p>
                    </div>
                </form>
            </div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
